import { IVideoMakerData } from "components/video-maker-block/video-maker-block.types";
import { questionsMetaDescriptionUpdateSet } from "constants/questions";
import Head from "next/head";
import { VideoCurriculum } from "services/api/api.types";
import {
  getRobotsMetaContent,
  getSubjectInBahasa,
  trimString
} from "utils/utils";

interface ITanyaQuestionIDSEOProps {
  title: string;
  subject: string;
  grade: string;
  topic: string;
  section: string;
  thumbnailURL: string;
  video_url: string | undefined;
  id: string;
  slug: string;
  upload_date: string;
  duration: string;
  transcript: string | null;
  curriculum: VideoCurriculum | null;
  videoMakerData: IVideoMakerData | null;
  // upvote_count: number | undefined;
}

export const TanyaQuestionIdSEO: React.FC<ITanyaQuestionIDSEOProps> = ({
  title,
  subject,
  grade,
  section,
  topic,
  thumbnailURL,
  video_url,
  id,
  slug,
  upload_date,
  duration,
  transcript,
  curriculum,
  // upvote_count,
  videoMakerData
}) => {
  const description =
    questionsMetaDescriptionUpdateSet[id] === 1
      ? `✔️ Tonton video solusi yang udah dicek kebenarannya. Solusi ngerjain latihan soal ${getSubjectInBahasa(
          subject
        )} kelas ${grade} materi ${topic}`
      : `Video solusi dari Tanya untuk jawab ${subject} - ${grade} | ${section} `;

  const learningVideoSchema = {
    "@context": "https://schema.org",
    "@type": ["VideoObject", "LearningResource"],
    name: title,
    description: `✔️ Tonton video solusi yang udah dicek kebenarannya. Solusi ngerjain latihan soal ${getSubjectInBahasa(
      subject
    )} kelas ${grade} materi ${topic}`,
    learningResourceType: "Problem Walkthrough",
    educationalLevel: getEducationLevel(Number(grade)),
    contentUrl: video_url,
    thumbnailUrl: [thumbnailURL],
    regionsAllowed: "ID",
    duration,
    transcript,
    uploadDate: upload_date,
    text: title,
    educationalAlignment: getEducationAlignment(curriculum, topic, grade),
    isFamilyFriendly: true,
    inLanguage: "id-ID",
    copyrightHolder: {
      "@type": "Organization",
      name: "CoLearn",
      url: process.env.NEXT_PUBLIC_SITE_URL,
      logo: `${process.env.NEXT_PUBLIC_SITE_URL}/assets/colearn_logo.png`,
      sameAs: [
        "https://www.facebook.com/colearn.id",
        "https://www.instagram.com/colearn.id/",
        "https://www.youtube.com/c/colearn",
        "https://www.linkedin.com/company/colearnid/",
        "https://twitter.com/colearn_id",
        "https://www.tiktok.com/@colearn.id"
      ]
    },
    ...(videoMakerData && {
      contributor: {
        "@type": "Person",
        name: videoMakerData.fullName,
        jobTitle: "Tutor",
        image: videoMakerData.image,
        description: videoMakerData.bio.replace(/(<([^>]+)>)/gi, ""),
        sameAs: [videoMakerData.socialLinks.linkedIn]
      }
    })
  };

  const QAndASchema = {
    "@context": "https://schema.org",
    "@type": "QAPage",
    mainEntity: {
      "@type": "Question",
      name: title,
      text: title,
      answerCount: 1,
      dateCreated: upload_date,
      author: {
        "@type": "Person",
        url: "https://colearn.id/blog/mengajar/gurujuara",
        name: "Colearn"
      },
      acceptedAnswer: {
        "@type": "Answer",
        text:
          transcript ??
          "Jawaban untuk pertanyaan ini dijelaskan dengan rinci dalam video solusi.",
        dateCreated: upload_date,
        author: {
          "@type": "Person",
          url: "https://colearn.id/blog/mengajar/gurujuara",
          name: "Colearn"
        },
        url: `${process.env.NEXT_PUBLIC_SITE_URL}/tanya/${id}/${slug}#answer`
        // upvoteCount: upvote_count
      }
    }
  };

  const titleToRender = trimString(title, 60);
  return (
    <>
      <Head>
        <title>{titleToRender}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={titleToRender} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={thumbnailURL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@CoLearn_ID" />
        <meta name="robots" content={getRobotsMetaContent()} />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SITE_URL}/tanya/${id}/${slug}`}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(learningVideoSchema)
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(QAndASchema)
          }}
        />
      </Head>
    </>
  );
};

const getEducationLevel = (grade: number) => {
  if (grade <= 6) {
    return "Beginner";
  } else if (grade === 7 || grade === 8 || grade === 9) {
    return `High School (ID)`;
  } else {
    return `${grade}th Grade (ID)`;
  }
};

const getEducationAlignment = (
  curriculum: VideoCurriculum | null,
  topic: string,
  grade: string
) => {
  if (curriculum === "Kurikulum Merdeka") {
    return {
      "@type": "AlignmentObject",
      educationalFramework: "Kurikulum Merdeka",
      targetName: `${topic} Kelas ${grade}`,
      targetUrl: "https://kurikulum.kemdikbud.go.id/kurikulum-merdeka/"
    };
  }
  // default return to Kurikulum 2013
  return {
    "@type": "AlignmentObject",
    educationalFramework: "K-13 Revisi",
    targetName: `${topic} Kelas ${grade}`,
    targetUrl: "https://kurikulum.kemdikbud.go.id/kurikulum-2013/"
  };
};
